/**
 * Live SSE quote tape across the top of the AppShell.
 * Subscribes to /api/v1/stream/quotes for the user's default watchlist symbols.
 * Falls back to polling if SSE drops.
 *
 * v0.5.7 AE-096 (UI truth sweep): when the default watchlist is empty the
 * tape seeds itself from held positions (largest by market value, both
 * ledgers). When there is genuinely nothing to show it renders nothing —
 * no perpetual "loading tape…", no dash tiles for a widget that will
 * never resolve.
 */
import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api';
import { useStream } from '../lib/useStream';
import { Sparkline } from './viz/Sparkline';

interface Tile {
  symbol: string;
  last: number;
  change_pct: number | null;
  trend: 'up' | 'down' | 'flat';
  ts?: string;
}

export function TickerTape() {
  const [symbols, setSymbols] = useState<string[]>([]);
  const [tiles, setTiles] = useState<Map<string, Tile>>(new Map());

  // Resolve which symbols to subscribe to: default watchlist first; when
  // that's empty, seed from held positions (top 8 by market value).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const wls = await api.watchlists.list();
        const def = wls.watchlists.find((w) => w.is_default) ?? wls.watchlists[0];
        let syms = def?.items.map((i) => i.symbol).slice(0, 8) ?? [];
        if (syms.length === 0) {
          const positions = await api.portfolio.positions('combined').catch(() => []);
          syms = [...positions]
            .sort((a, b) => (b.market_value ?? 0) - (a.market_value ?? 0))
            .map((p) => p.symbol)
            .filter((s, i, arr) => arr.indexOf(s) === i)
            .slice(0, 8);
        }
        if (alive) setSymbols(syms);
      } catch {
        // ignore
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // v0.5.8 AE-106: one 5-day close series per tile, fetched ONCE when the
  // symbol set resolves — never refetched on quote ticks. Missing history
  // just means no sparkline behind that tile.
  const [sparks, setSparks] = useState<Record<string, number[]>>({});
  useEffect(() => {
    if (symbols.length === 0) return;
    let alive = true;
    Promise.all(
      symbols.map((s) =>
        api.market
          .history(s, '5d', '1d')
          .then((r) => [s, (r.bars ?? []).map((b) => b.close)] as const)
          .catch(() => [s, [] as number[]] as const),
      ),
    ).then((entries) => {
      if (alive) setSparks(Object.fromEntries(entries));
    });
    return () => {
      alive = false;
    };
  }, [symbols]);

  const url = useMemo(
    () => (symbols.length ? `/api/v1/stream/quotes?symbols=${symbols.join(',')}` : ''),
    [symbols],
  );

  useStream({
    url,
    enabled: symbols.length > 0,
    onEvent: ({ event, data }) => {
      if (event !== 'quote') return;
      const d = data as Record<string, unknown>;
      const sym = String(d.symbol ?? '');
      const last = Number(d.last ?? 0);
      const cp = d.change_pct === null || d.change_pct === undefined ? null : Number(d.change_pct);
      const trend: Tile['trend'] =
        cp === null ? 'flat' : cp > 0 ? 'up' : cp < 0 ? 'down' : 'flat';
      setTiles((prev) => {
        const next = new Map(prev);
        next.set(sym, { symbol: sym, last, change_pct: cp, trend, ts: String(d.timestamp ?? '') });
        return next;
      });
    },
  });

  // Nothing to track (no watchlist, no positions): the widget removes
  // itself rather than promising a tape that will never load.
  if (symbols.length === 0) {
    return null;
  }

  return (
    <div className="overflow-hidden whitespace-nowrap text-[11px] num">
      <div className="inline-flex gap-4 items-center">
        {symbols.map((s) => {
          const t = tiles.get(s);
          const closes = sparks[s] ?? [];
          if (!t) {
            // No live quote yet — the 5d trend (when we have one) still
            // gives the tile a shape while the stream warms up.
            return (
              <span key={s} className="inline-flex items-center gap-1 text-ink-dim">
                {s} —
                {closes.length >= 2 && (
                  <Sparkline values={closes} width={36} height={12} dot={false} strokeWidth={1} tone="mute" aria-label={`${s} 5-day trend`} />
                )}
              </span>
            );
          }
          return (
            <span
              key={s}
              className={`inline-flex items-center gap-1 ${
                t.trend === 'up'
                  ? 'text-ok'
                  : t.trend === 'down'
                  ? 'text-bad'
                  : 'text-ink-mute'
              }`}
            >
              <span className="font-semibold">{t.symbol}</span> ${t.last.toFixed(2)}
              {t.change_pct !== null && (
                <span className="ml-1">
                  {t.change_pct >= 0 ? '+' : ''}
                  {t.change_pct.toFixed(2)}%
                </span>
              )}
              {closes.length >= 2 && (
                <Sparkline
                  values={closes}
                  width={36}
                  height={12}
                  dot={false}
                  strokeWidth={1}
                  aria-label={`${s} 5-day trend`}
                />
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
